// src/pages/ShopCategory.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './ShopCategory.css';

const ShopCategory = () => {
  const [categories, setCategories] = useState([]);
  const [categoryLabels, setCategoryLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fallback images for categories if no image is available
  const getCategoryImage = (category) => {
    const imageMap = {
      'shirts': 'https://images.unsplash.com/photo-1596755094514-f87e34085b2c?w=600&h=800&fit=crop',
      'clothing': 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=600&h=800&fit=crop',
      't-shirts': 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=600&h=800&fit=crop',
      't-shirts-polos': 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=600&h=800&fit=crop',
      'trousers': 'https://images.unsplash.com/photo-1473966968600-fa801b869a1a?w=600&h=800&fit=crop',
      'trousers-jeans': 'https://images.unsplash.com/photo-1473966968600-fa801b869a1a?w=600&h=800&fit=crop',
      'jeans': 'https://images.unsplash.com/photo-1473966968600-fa801b869a1a?w=600&h=800&fit=crop',
      'eastern': 'https://images.unsplash.com/photo-1611312449408-fcece27cdbb7?w=600&h=800&fit=crop',
      'eastern-wear': 'https://images.unsplash.com/photo-1611312449408-fcece27cdbb7?w=600&h=800&fit=crop',
      'electronics': 'https://images.unsplash.com/photo-1498049794561-7780e7231661?w=600&h=800&fit=crop',
      'books': 'https://images.unsplash.com/photo-1495446815901-a7297e633e8d?w=600&h=800&fit=crop',
      'accessories': 'https://images.unsplash.com/photo-1523170335258-f5ed11844a49?w=600&h=800&fit=crop',
      'home': 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&h=800&fit=crop',
      'sports': 'https://images.unsplash.com/photo-1517466787929-bc90951d0974?w=600&h=800&fit=crop',
      'jewelry': 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=600&h=800&fit=crop'
    };
    return imageMap[category] || 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=600&h=800&fit=crop';
  };

  // Get a nice display name for category
  const getCategoryDisplayName = (categoryKey) => {
    return categoryLabels[categoryKey] || categoryKey
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Load categories from database
  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://demo-site.creativepulse.pk/demo-api/public/get-settings.php');
      const data = await response.json();
      
      if (data.success && data.settings) {
        let categoriesArray = [];
        let labelsObj = {};
        
        // Parse categories from settings
        if (data.settings.categories) {
          try {
            const parsed = JSON.parse(data.settings.categories);
            categoriesArray = parsed;
          } catch (e) {
            console.error('Error parsing categories:', e);
          }
        }
        
        // Parse category labels
        if (data.settings.category_labels) {
          try {
            const parsedLabels = JSON.parse(data.settings.category_labels);
            labelsObj = parsedLabels;
          } catch (e) {
            console.error('Error parsing category labels:', e);
          }
        }
        
        // If no categories found, use fallback
        if (categoriesArray.length === 0) {
          categoriesArray = ['shirts', 't-shirts', 'trousers', 'eastern'];
          labelsObj = {
            shirts: 'Shirts',
            't-shirts': 'T-Shirts & Polos',
            trousers: 'Trousers & Jeans',
            eastern: 'Eastern Wear'
          };
        }
        
        setCategories(categoriesArray);
        setCategoryLabels(labelsObj);
        setError(null);
      } else {
        // Fallback categories if API fails
        setCategories(['shirts', 't-shirts', 'trousers', 'eastern']);
        setCategoryLabels({
          shirts: 'Shirts',
          't-shirts': 'T-Shirts & Polos',
          trousers: 'Trousers & Jeans',
          eastern: 'Eastern Wear'
        });
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      // Fallback categories
      setCategories(['shirts', 't-shirts', 'trousers', 'eastern']);
      setCategoryLabels({
        shirts: 'Shirts',
        't-shirts': 'T-Shirts & Polos',
        trousers: 'Trousers & Jeans',
        eastern: 'Eastern Wear'
      });
      setError('Failed to load categories. Showing default categories.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  // If loading, show skeleton or loading state
  if (loading) {
    return (
      <div className="shop-category-page">
        <div className="shop-category-container">
          <div className="shop-category-header">
            <h2 className="shop-category-title">Shop by Category</h2>
            <p className="shop-category-subtitle">Find the look that fits your day.</p>
          </div>
          <div className="categories-grid">
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="category-card loading">
                <div className="category-image-wrapper">
                  <div className="category-image-skeleton"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="shop-category-page">
      <div className="shop-category-container">
        {/* Header */}
        <div className="shop-category-header">
          <h2 className="shop-category-title">Shop by Category</h2>
          <p className="shop-category-subtitle">Find the look that fits your day.</p>
          {error && <p className="category-error">{error}</p>}
        </div>

        {/* Categories Grid */}
        <div className="categories-grid">
          {categories.map((category) => {
            const displayName = getCategoryDisplayName(category);
            // Create slug for the category link
            const slug = category.toLowerCase().replace(/[& ]/g, '-');
            
            return (
              <Link 
                to={`/shop?category=${slug}`} 
                key={category} 
                className="category-card"
              >
                <div className="category-image-wrapper">
                  <img 
                    src={getCategoryImage(category)} 
                    alt={displayName}
                    className="category-image"
                    loading="lazy"
                  />
                  <div className="category-overlay"></div>
                  <div className="category-info">
                    <h3 className="category-name">{displayName}</h3>
                    <span className="shop-now-link">
                      Shop Now 
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="arrow-icon">
                        <path d="M5 12h14"></path>
                        <path d="m12 5 7 7-7 7"></path>
                      </svg>
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ShopCategory;